import { useMemo, useCallback } from 'react';
import { RootState } from 'redux/rootReducer';
import { useSelector } from 'react-redux';
import capitalize from 'lodash.capitalize';
import {
  selectStreamingCustomers,
  selectDownloadCustomers,
} from 'redux/reducers/customers';

import { ITopicFilterState, TopicFilterNames } from '../shared';
const useFilterList = (
  topicFilterState: ITopicFilterState,
  validFilterNames: TopicFilterNames[]
): any => {
  const countries = useSelector((state: RootState) => state.countries.data);
  const exCountries = useSelector((state: RootState) => state.exCountries.data);
  const streamingCustomers = useSelector(selectStreamingCustomers);
  const downloadCustomers = useSelector(selectDownloadCustomers);
  const regions = useSelector((state: RootState) => state.regions.data);
  const customRegions = Object.values(
    useSelector((state: RootState) => state.customRegions.data)
  );
  const genres = useSelector((state: RootState) => state.genres.data);

  const findCountry = (countryId: any): ((country: any) => boolean) => {
    return (country: any): boolean => country.id === countryId;
  };
  const findCustomer = (customerId: any): ((customer: any) => boolean) => {
    return (customer: any): boolean => customer.id === customerId;
  };
  const findRegion = (regionId: any): ((region: any) => boolean) => {
    return (region: any): boolean => region.id === regionId;
  };
  const findGenre = (genreId: any): ((genre: any) => boolean) => {
    return (genre: any): boolean => genre.genreCode === genreId;
  };

  const resolveFilterValue = useCallback(
    (name: TopicFilterNames, value: any): any | undefined => {
      switch (name) {
        case 'customRegion': {
          const region = customRegions.find(findRegion(value));
          return region ?? { displayName: 'All Custom Regions' };
        }
        case 'region': {
          const region = regions.find(findRegion(value));
          return region ?? { displayName: 'All Regions' };
        }
        case 'country': {
          const country = countries.find(findCountry(value));
          if (country) {
            return country;
          }
          const exCountry = exCountries.find(findCountry(value));
          if (exCountry) {
            return exCountry;
          }
          return { displayName: 'Worldwide' };
        }
        case 'streaming': {
          const streamingCustomer = streamingCustomers.find(
            findCustomer(value)
          );
          return streamingCustomer ?? { displayName: 'All DSPs' };
        }
        case 'download': {
          const streamingCustomer = downloadCustomers.find(findCustomer(value));
          return streamingCustomer?.displayName ?? { displayName: 'All DSPs' };
        }
        case 'media': {
          return (value && capitalize(value)) ?? undefined;
        }
        case 'release': {
          return (value && capitalize(value)) ?? undefined;
        }
        case 'genre': {
          const genre = genres.find(findGenre(value));
          return genre?.genreName ?? undefined;
        }
        default:
          return undefined;
      }
    },
    [
      countries,
      exCountries,
      regions,
      customRegions,
      genres,
      streamingCustomers,
      downloadCustomers,
    ]
  );

  return useMemo(() => {
    const obj: any = {};
    validFilterNames.forEach((name: keyof ITopicFilterState) => {
      const filterValue = resolveFilterValue(name, topicFilterState[name]);
      obj[name.toString()] = filterValue;
    });

    return obj;
  }, [resolveFilterValue, topicFilterState, validFilterNames]);
};

export default useFilterList;
